<template>
  <div
    class="table-container portfolio-table"
    v-if="t_portfolio"
    :class="{ editMode }"
  >
    <div :class="[{ noneditable: !editMode }, 'table-controls']">
      <b-button
        variant="blank"
        class="link-button"
        @click="toggleEditMode"
        :disabled="
          (editMode &&
            weightsSum &&
            weightsSum.toFixed(0) !== '100' &&
            !cash) ||
          (editMode && this.existTickers) ||
          (editMode && this.tickersNonExistList.length != 0) ||
          addRow !== null
        "
        ><save-icon class="link-icon" v-if="editMode" :size="18" /><pencil-icon
          class="link-icon"
          :size="18"
          v-else
        />
        {{
          $t(`portfolioManager.table.${editMode ? "saveChanges" : "editMode"}`)
        }}</b-button
      >
      <span class="link-button" @click="handleAddStockTitle" v-if="editMode">
        <add-icon class="link-icon" :size="18" />
        {{ $t("portfolioManager.table.addStock") }}
      </span>
      <span
        v-if="editMode && !hasReserve"
        class="link-button"
        @click="handleAddCashReserve"
      >
        <dolar-icon :size="18" />
        {{ $t("portfolioManager.addCashReserve") }}
      </span>
      <span class="link-button" v-if="editMode" @click="handleCancelChanges">{{
        $t("general.cancel")
      }}</span>

      <div
        :class="[
          { error: weightsSum.toFixed(0) !== '100' },
          'sum-control link-button',
        ]"
        v-if="editMode && !cash && !isWholeStocks"
      >
        <span>{{ $t("general.sum") }}:</span>
        {{ weightsSum ? weightsSum : "-" }}
      </div>
      <div class="sum-control link-button" v-if="capitalSum">
        <span>
          {{ $t("portfolioManager.table.assetsValue") }}: ${{
            capitalSum.toFixed(2)
          }}
        </span>
      </div>
    </div>
    <b-table
      :items="t_portfolio"
      :per-page="perPage"
      :current-page="currentPage"
      :fields="fields"
      :busy="isOptimizingPortfolio"
      :tbody-tr-class="rowClass"
      sort-icon-left
      :sort-by.sync="sorting"
      :sort-desc.sync="directionDesc"
      :no-local-sorting="editMode"
    >
      <template v-slot:top-row="data" v-if="addRow !== null">
        <td>
          <select-box
            v-model="addRow.ticker"
            @blur="
              handleFocusOut(true);
              checkExistTickers();
            "
            :hasError="errorTicker"
            :options="tickerOptions"
            label="ticker"
            :clearable="false"
          />
        </td>
        <td class="input-cell">
          <simple-form-input
            v-model="addRow[data.fields[1].key]"
            type="number"
            @blur="handleFocusOut(true)"
            :hasError="errorValue"
            noMessage
          />
        </td>
        <td>
          <div class="removing-col" @click="checkExistTickers()">
            <remove-icon @click="addRow = null" :size="22" />
          </div>
        </td>
      </template>
      <template #cell(ticker)="data">
        <select-box
          v-if="editMode && data.item.ticker !== RESERVE_TICKER"
          v-model="data.item.ticker"
          :options="tickerOptions"
          label="ticker"
          :clearable="false"
        />

        <span v-else>{{
          data.item.ticker === RESERVE_TICKER
            ? $t("portfolioManager.table.cashReserve")
            : data.item.ticker
        }}</span>
      </template>
      <template #cell(weight)="data">
        <simple-form-input
          v-model="data.item.weight"
          v-if="editMode"
          type="number"
          noMessage
        >
          <template v-slot:trailingIcon> % </template>
        </simple-form-input>
        <span v-else>{{ `${data.item.weight.toFixed(2)} %` }}</span>
      </template>
      <template #cell(capital)="data">
        <simple-form-input
          v-model="data.item.capital"
          v-if="editMode"
          type="number"
          noMessage
        >
          <template v-slot:trailingIcon>
            <currency-usd :size="18" />
          </template>
        </simple-form-input>
        <span v-else>${{ data.item.capital.toFixed(2) }}</span>
      </template>
      <template #cell(amount)="data">
        <simple-form-input
          v-model="
            data.item[
              data.item.ticker !== RESERVE_TICKER ? 'amount' : 'capital'
            ]
          "
          v-if="editMode"
          type="number"
          noMessage
        >
          <template
            v-slot:trailingIcon
            v-if="data.item.ticker === RESERVE_TICKER"
          >
            <currency-usd :size="18" />
          </template>
        </simple-form-input>
        <span v-else>{{
          `${data.item.ticker === RESERVE_TICKER ? "$" : ""}${
            data.item.ticker === RESERVE_TICKER
              ? data.item.capital.toFixed(2)
              : !useFractions
              ? Math.floor(data.item.amount)
              : data.item.amount.toFixed(4)
          }`
        }}</span>
      </template>
      <template #cell(delete)="data">
        <div class="removing-col" @click="checkExistTickers()">
          <remove-icon v-if="editMode" @click="removeRow(data.item.ticker)" />
          <delete-icon
            v-else-if="!removedTickers.includes(data.item.ticker)"
            :size="22"
            @click="removeTickers(data.item.ticker)"
          />
          <delete-icon
            v-else
            @click="restoreTicker(data.item.ticker)"
            :size="22"
          />
        </div>
      </template>
    </b-table>
    <table-footer
      :perPage="perPage"
      :page="currentPage"
      :pageSizes="pageSizes"
      :totalRows="t_portfolio.length"
      @pageChange="(val) => (this.currentPage = val)"
      @perPageChange="(val) => (this.perPage = val)"
    />
    <div class="nonExist-root">
      <exclamation-icon
        v-if="this.tickersNonExistList.length != 0"
        class="exclamation"
      />
      <span class="missing-tickers">
        {{
          this.tickersNonExistList.length != 1 &&
          this.tickersNonExistList.length != 0
            ? this.$i18n.t("portfolioManager.table.tickersExist", {
                tickers: this.tickersNonExistList.join(", "),
              })
            : this.tickersNonExistList.length != 0
            ? this.$i18n.t("portfolioManager.table.tickerExist", {
                ticker: this.tickersNonExistList.join(", "),
              })
            : ""
        }}
      </span>
    </div>
    <div v-if="blockedAssetsConflict.length !== 0" class="info-container">
      <exclamation-icon :size="18" />
      {{
        $t("portfolioManager.table.blockedAssetsConflict", {
          assets: blockedAssetsConflict.join(", "),
        })
      }}
    </div>
  </div>
</template>

<script>
import {
  compareObjects,
  composeTickerRuleList,
  multiply100,
} from "../../utils/helper";
import { mapActions, mapGetters, mapMutations } from "vuex";
import AddIcon from "vue-material-design-icons/PlusCircleOutline.vue";
import CurrencyUsd from "vue-material-design-icons/CurrencyUsd.vue";
import DeleteIcon from "vue-material-design-icons/SwapVerticalCircleOutline.vue";
import DolarIcon from "vue-material-design-icons/CurrencyUsd.vue";
import ExclamationIcon from "vue-material-design-icons/AlertCircleOutline.vue";
import MainAlert from "../alerts/MainAlert.vue";
import PencilIcon from "vue-material-design-icons/Pencil.vue";
import { RESERVE_TICKER } from "../../utils/constants";
import RemoveIcon from "vue-material-design-icons/DeleteOutline.vue";
import SaveIcon from "vue-material-design-icons/ContentSave.vue";
import SelectBox from "../inputs/SelectBox.vue";
import SimpleFormInput from "../inputs/SimpleFormInput.vue";
import TableFooter from "./tableComponents/TableFooter.vue";
export default {
  components: {
    AddIcon,
    DeleteIcon,
    DolarIcon,
    RemoveIcon,
    TableFooter,
    SaveIcon,
    SimpleFormInput,
    PencilIcon,
    CurrencyUsd,
    ExclamationIcon,
    SelectBox,
  },
  name: "portfolio-table",
  props: {
    portfolio: Object,
  },
  data() {
    return {
      t_portfolio: null,
      perPage: 5,
      currentPage: 1,
      pageSizes: [5, 10, 20],
      sorting: null,
      directionDesc: false,
      addRow: null,
      unfocused: false,
      errorTicker: false,
      errorValue: false,
      alert: false,
      poolAlert: false,
      RESERVE_TICKER,
    };
  },
  methods: {
    ...mapMutations("portfolioManagerStore", [
      "removeTicker",
      "restoreTicker",
      "setEditMode",
    ]),
    ...mapActions("portfolioManagerStore", [
      "portfolioConversion",
      "checkTickers",
      "calculatePortfolioMetrics",
    ]),
    handleAddCashReserve() {
      this.t_portfolio = [
        {
          ticker: RESERVE_TICKER,
          capital: this.t_portfolio[0].capital ? 1 : 0,
          amount: 0,
          weight: 0,
        },
        ...this.t_portfolio,
      ];
    },
    sortPortfolio() {
      if (this.sorting !== null) {
        if (this.directionDesc) {
          this.t_portfolio.sort((a, b) =>
            a[this.sorting] < b[this.sorting] ? 1 : -1
          );
        } else {
          this.t_portfolio.sort((a, b) =>
            a[this.sorting] > b[this.sorting] ? 1 : -1
          );
        }
      }
    },
    toggleEditMode() {
      if (!this.editMode) {
        this.setEditMode(true);
      } else {
        if (
          Number(this.weightsSum.toFixed(0)) === 100 ||
          this.t_portfolio[0].capital
        ) {
          this.setEditMode(false);
          this.t_portfolio.sort((a, b) => (a.ticker > b.ticker ? 1 : -1));
          if (
            !compareObjects(
              this.t_portfolio,
              this.portfolio.portfolio_input.custom_portfolio
            )
          ) {
            const portfolio = this.t_portfolio.map((item) => ({
              ...item,
              weight: item.weight / 100,
            }));
            this.portfolioConversion({
              portfolio,
              isConversingPointDetail: false,
            }).then(() =>
              this.calculatePortfolioMetrics({
                saveNewVersion: false,
                portfolio: portfolio[0].capital ? null : portfolio,
              }).catch((err) => {
                this.$toast.error({
                  component: MainAlert,
                  props: {
                    message: err.response.data.detail,
                  },
                });
              })
            );
          }
          if (this.portfolio) {
            this.$nextTick(() => this.invokeTickersNotInPoolAlert());
          }
        } else {
          this.$toast.error({
            component: MainAlert,
            props: {
              message: this.$i18n.t("portfolioManager.table.wrongSum"),
            },
          });
        }
      }
    },
    handleAddStockTitle() {
      if (this.addRow === null && !this.isAddRowEmpty) {
        this.addRow = { ticker: "", weight: "", capital: 0, amount: "" };
      } else {
        this.errorTicker = this.addRow.ticker === "";
        this.errorValue =
          this.addRow.weight === "" &&
          this.addRow.amount === "" &&
          this.addRow.capital === 0;
        setTimeout(() => {
          this.errorTicker = false;
          this.errorValue = false;
        }, 170);
      }
    },
    handleFocusOut(value) {
      this.unfocused = value;
    },
    checkExistTickers() {
      let tickers_list = [];
      if (this.addRow) {
        tickers_list.push(this.addRow.ticker);
      } else {
        this.t_portfolio.forEach((value) => {
          tickers_list.push(value.ticker);
        });
      }
      this.checkTickers(tickers_list);
    },
    handleCancelChanges() {
      this.populateTable();
      this.addRow = null;
      this.setEditMode(false);
      this.addRow = null;
    },
    handleClearPortfolio() {
      this.setPortfolio(null);
    },
    removeTickers(ticker) {
      this.removeTicker(ticker);
    },
    removeRow(rowIndex) {
      this.t_portfolio = this.t_portfolio.filter(
        (el) => el.ticker !== rowIndex
      );
      if ((this.currentPage - 1) * this.perPage >= this.t_portfolio.length) {
        this.currentPage = this.currentPage - 1;
      }
    },
    populateTable() {
      this.currentPage = 1;
      const custom_portfolio =
        this.portfolio.portfolio_input.custom_portfolio.map((item) => ({
          ...item,
          weight: item.weight ? multiply100(item.weight) : null,
        }));
      this.t_portfolio = JSON.parse(JSON.stringify(custom_portfolio));
      this.invokeTickersNotInPoolAlert();
    },
    rowClass(item, type) {
      if (!item || type !== "row") return;
      if (this.removedTickers.includes(item.ticker)) return "deleted-row";
      if (this.tickersNonExistList.includes(item.ticker)) return "red-row";
    },
    invokeTickersNotInPoolAlert() {
      const portfolioTickers = this.t_portfolio
        .map((obj) => obj.ticker)
        .filter((t) => t !== RESERVE_TICKER);
      const missingTickers = this.isMissingTickerInPool.missingTickers.filter(
        (ticker) => portfolioTickers.includes(ticker)
      );
      if (
        this.portfolio &&
        missingTickers.length !== 0 &&
        !this.poolAlert &&
        this.tickersNonExistList.length === 0
      ) {
        this.poolAlert = true;
        this.$toast.info({
          component: MainAlert,
          props: {
            message: this.$i18n.t(
              missingTickers.length > 1
                ? "errors.tickersNotInPool"
                : "errors.tickerNotInPool",
              {
                missingTickers: missingTickers.join(", "),
              }
            ),
          },
        });
        setTimeout(() => (this.poolAlert = false), 500);
      }
    },
    invokeAddTickerNotInSP100Alert() {
      this.$toast.info({
        component: MainAlert,
        props: {
          message: this.$i18n.t("errors.tickerNotExist", {
            ticker: this.addRow.ticker,
          }),
        },
      });
    },
    invokeTickersNotInSP100Alert() {
      if (this.tickersNonExistList.length != 0) {
        if (this.tickersNonExistList.length != 1) {
          this.$toast.info({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.tickersNotExist", {
                ticker: this.tickersNonExistList.join(", "),
              }),
            },
          });
        } else {
          this.$toast.info({
            component: MainAlert,
            props: {
              message: this.$i18n.t("errors.tickerNotExist", {
                ticker: this.tickersNonExistList.join(", "),
              }),
            },
          });
        }
      }
    },
    isEmpty(data) {
      return !(
        data.ticker !== "" &&
        (data.weight !== "" || data.amount !== "" || data.capital !== 0)
      );
    },
  },
  computed: {
    ...mapGetters("portfolioManagerStore", [
      "removedTickers",
      "isOptimizingPortfolio",
      "cash",
      "isWholeStocks",
      "useFractions",
      "isMissingTickerInPool",
      "selectedPool",
      "editMode",
      "tickersNonExistList",
      "missingTickerFinished",
      "existTickers",
      "getPoolSP100",
      "rules",
      "pools",
      "availableTickers",
    ]),
    tickerOptions() {
      return this.availableTickers ? Object.keys(this.availableTickers) : [];
    },
    hasReserve() {
      return (
        this.t_portfolio.findIndex((el) => el.ticker === RESERVE_TICKER) >= 0
      );
    },
    isAddRowEmpty() {
      let empty = false;
      this.t_portfolio.map((item) => {
        if (this.isEmpty(item)) {
          empty = true;
        }
      });
      return empty;
    },
    weightsSum() {
      const sum = this.t_portfolio.reduce(
        (acc, row) => acc + (row.weight ? parseFloat(row.weight) : 0),
        0
      );

      return Number(sum.toFixed(2));
    },
    capitalSum() {
      return this.t_portfolio[0].capital && this.cash
        ? this.t_portfolio.reduce((acc, curr) => acc + curr.capital, 0)
        : false;
    },
    hasAmounts() {
      return (
        this.portfolio &&
        this.portfolio.portfolio_input.custom_portfolio[0].amount !== undefined
      );
    },
    fields() {
      const defaultFields = [
        {
          key: "ticker",
          label: this.$i18n.t("portfolioManager.table.stock"),
          sortable: !this.editMode,
        },
        {
          key: "weight",
          label: this.$i18n.t("portfolioManager.table.weight"),
          sortable: !this.editMode,
          tdClass: this.editMode ? "input-cell" : "",
          thClass: this.editMode ? "input-cell" : "",
        },
        {
          key: "delete",
          label: this.$i18n.t("portfolioManager.table.alternate"),
          thClass: "text-right",
        },
      ];
      if (this.isWholeStocks) {
        const wholeFields = defaultFields;
        wholeFields[1].key = "amount";
        wholeFields[1].label = this.$i18n.t(
          "portfolioManager.table.wholeStocks"
        );
        return wholeFields;
      } else if (this.cash !== null && this.t_portfolio[0].capital) {
        const cashFields = defaultFields;
        cashFields[1].key = "capital";
        cashFields[1].label = this.$i18n.t(
          "portfolioManager.table.cashRepresentation"
        );
        return cashFields;
      } else {
        return defaultFields;
      }
    },
    blockedAssetsConflict() {
      const tickersRules = composeTickerRuleList(this.rules);
      if (this.removedTickers.length !== 0 && tickersRules.length) {
        const portfolioData =
          this.portfolio.portfolio_input.custom_portfolio.map(
            ({ ticker }) => ticker
          );
        return tickersRules.filter(
          (value) =>
            portfolioData.includes(value) &&
            !this.removedTickers.includes(value)
        );
      }
      return [];
    },
  },
  mounted() {
    this.populateTable();
    this.addRow = null;
    this.alert = true;
  },
  watch: {
    portfolio: {
      handler() {
        this.populateTable();
      },
      deep: true,
    },
    t_portfolio: {
      handler() {
        this.checkExistTickers();
      },
      deep: true,
    },
    existTickers: {
      handler() {
        if (this.missingTickerFinished && this.existTickers) {
          this.invokeAddTickerNotInSP100Alert();
        }
      },
      deep: true,
    },
    tickersNonExistList: {
      handler() {
        if (this.alert && this.missingTickerFinished) {
          this.invokeTickersNotInSP100Alert();
          this.alert = false;
        }
      },
      deep: true,
    },
    addRow: {
      handler(newValue, oldValue) {
        if (oldValue !== null && newValue !== null && this.unfocused) {
          this.handleFocusOut(false);
        }
      },
      deep: true,
    },
    unfocused() {
      if (this.unfocused) {
        if (this.addRow !== null && !this.isEmpty(this.addRow)) {
          const index = (this.currentPage - 1) * this.perPage;
          this.t_portfolio.splice(index, 0, this.addRow);
          this.addRow = null;
          this.handleFocusOut(false);
        }
      }
    },
    selectedPool() {
      this.invokeTickersNotInPoolAlert();
    },
    editMode(val) {
      if (val) {
        this.sortPortfolio();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.portfolio-table {
  padding: 0;
  .table.b-table[aria-busy="true"] {
    .removing-col {
      touch-action: none;
    }
  }
  .cash-reserve-root {
    display: flex;
    align-items: center;
    padding: 5px 0 10px 20px;
    border-bottom: 1px solid #e2eafe;
    .reserve-title {
      color: $primaryColor;
      font-size: $s-font;
      font-weight: 500;
      margin-right: 50px;
    }
    .input-root {
      width: 40%;
    }
  }
  .nonExist-root {
    display: flex;
    padding: 5px 0 10px 20px;
    padding-left: 0;
    .missing-tickers {
      color: $errorRedBorder;
      font-size: $s-font;
      font-weight: 500;
    }
    .exclamation {
      color: $errorRedBorder;
      font-size: 22;
    }
  }
}
.info-container {
  color: $primaryColor;
  font-size: toRem(13);
}
.removing-col {
  text-align: right;
  min-width: toRem(18);
  svg,
  span {
    cursor: pointer;
    min-width: toRem(15);
  }
}
.table-controls {
  color: $primaryColor;
  display: flex;
  align-items: center;
  .link-button {
    cursor: pointer;
    &:first-child {
      padding-left: 0;
    }
    display: flex;
    padding: 0 0.5rem;
    align-items: center;
    .link-icon {
      margin-right: toRem(3);
    }
  }
  .sum-control {
    margin-left: auto;
    align-items: baseline;
    cursor: context-menu;
    &.error {
      color: #cf2323;
      span {
        color: #cf2323;
      }
    }
    span {
      font-weight: 500;
      color: #000;
      font-size: $s-font;
      padding-right: toRem(10);
    }
  }
}
</style>